import dayjs from 'dayjs';
import {makeAutoObservable, observable, runInAction} from 'mobx';

import type {CoachHistoryExpandedCommon} from '../../api/coachHistory/types';
import type {ApiListResponse} from '../../api/types';
import type {
  CoreClientConferencesAvailableSlotsParams,
  CorePublicConferencesAvailableSlotsParams,
} from '../../api/types/core/methods';
import type {PayloadAction} from '../../utils/events';
import type {WithBusyManager} from '../../utils/store';
import type {CalendarController} from './Calendar.controller';
import type {Coach, DaySlots, TimeSlot, TimeslotData} from './types';

import {apiRequest} from '../../api/utils/apiRequest';
import {chunkArray} from '../../utils/array';
import {not} from '../../utils/conditionalUtils';
import {
  datetimeObjToISOString,
  dateTimeObjToTimestamp,
  ISOStringToDatetimeObj,
} from '../../utils/datetime';
import {SCHEDULE_SLOT_DURATION} from '../../utils/entity/user';
import {logger} from '../../utils/logger';
import {AsyncBusyManager, InitialLoadManager} from '../../utils/store';
import {CoachModel} from './Coach.model';
import {TimeslotModel} from './Timeslot.model';
import {DATE_KEY_FORMAT} from './utils/constants';

export class TimeslotsStore implements WithBusyManager<TimeslotsStore.BusyKey> {
  allSlots: TimeslotData[] = [];
  readonly busy = new AsyncBusyManager<TimeslotsStore.BusyKey>();
  readonly initialLoad = new InitialLoadManager();
  isMakingSlotsDone = false;
  newCoachSlots: TimeslotData[] = [];
  renderSlots = new Map<string, TimeslotModel[][]>();
  selected: null | TimeslotModel = null;
  private calendarStore: CalendarController;
  private isMakingSlotsInProgress = false;
  private isTodayAvailableForBooking = true;
  private minBookingLeadMinutes = 0;

  constructor(calendar: CalendarController) {
    this.calendarStore = calendar;

    this.busy.register('makeSlots');

    makeAutoObservable(
      this,
      {
        allSlots: false,
        renderSlots: observable.ref,
      },
      {
        autoBind: true,
      },
    );
  }

  get renderNewCoachSlots() {
    return this.mapTimeslotDataToRenderSlotsMap(this.newCoachSlots, true);
  }

  get todaySlots() {
    const key = dayjs(this.calendarStore.selectedDate).format(DATE_KEY_FORMAT);

    const wanted = this.renderSlots.get(key);

    return wanted ? wanted : [];
  }

  clearSelectedSlot() {
    if (this.selected) {
      this.selected = null;
    }
  }

  async makeSlots({
    coaches,
    duration,
    externalAuthData,
    isSilent = false,
    newCoachHistory,
  }: {
    coaches: CoachModel[];
    duration?: number;
    externalAuthData?: TimeslotsStore.ExternalAuthData;
    isSilent?: boolean;
    newCoachHistory?: CoachHistoryExpandedCommon;
  }) {
    try {
      if (this.isMakingSlotsInProgress) {
        return;
      }

      if (!isSilent) {
        this.busy.setReason('makeSlots', true);
      }

      this.isMakingSlotsInProgress = true;
      this.isMakingSlotsDone = false;

      if (externalAuthData) {
        await this.fetchExternalSlots({duration, externalAuthData});
      } else {
        await this.fetchSlots({
          coaches,
          coachHistory: newCoachHistory,
          duration,
        });

        if (newCoachHistory?.new_coach_id) {
          await this.fetchNewCoachSlots(newCoachHistory);
        }
      }

      this.compute({coachHistory: newCoachHistory});

      runInAction(() => {
        this.isMakingSlotsDone = true;
      });
    } finally {
      runInAction(() => {
        this.isMakingSlotsInProgress = false;
      });

      if (!isSilent) {
        this.busy.setReason('makeSlots', false);
      }
    }
  }

  onSlotSelect(action: PayloadAction<TimeslotModel>) {
    logger.event('schedule_slot_select_tap');

    const slot = action.payload;

    if (this.selected) {
      this.selected.isSelected.setFalse();
    }

    if (slot) {
      slot.isSelected.setTrue();
      this.selected = slot;
    }
  }

  setIsTodayAvailableForBooking(value: boolean) {
    this.isTodayAvailableForBooking = value;
  }

  setMinBookingLeadMinutes(minutes: number) {
    this.minBookingLeadMinutes = minutes;
  }

  private compute({coachHistory}: {coachHistory?: CoachHistoryExpandedCommon}) {
    const renderSlots = this.mapTimeslotDataToRenderSlotsMap(
      this.allSlots,
      Boolean(this.newCoachSlots),
    );

    if (coachHistory) {
      if (this.newCoachSlots.length) {
        const renderNewCoachSlots = this.renderNewCoachSlots;
        const mergedSlots = this.mergeSlotsWithNewCoach(
          renderSlots,
          renderNewCoachSlots,
        );

        runInAction(() => {
          this.renderSlots = mergedSlots;
        });
      } else {
        // If we don't have new coach, we should remove coverage days because the program is paused
        const {end_date, start_date} = coachHistory;
        const coverageStartDate = dayjs(dateTimeObjToTimestamp(start_date));
        const coverageEndDate = dayjs(dateTimeObjToTimestamp(end_date));

        for (const [key, timeslots] of renderSlots.entries()) {
          const date = timeslots[0]?.[0]?.startDateDjs;

          if (
            !date ||
            (date.isAfter(coverageStartDate) && date.isBefore(coverageEndDate))
          ) {
            renderSlots.delete(key);
          }
        }

        runInAction(() => {
          this.renderSlots = renderSlots;
        });
      }
    } else {
      runInAction(() => {
        this.renderSlots = renderSlots;
      });
    }

    this.updateSelectedSlotIfNeeded();
  }

  private async fetchExternalSlots({
    externalAuthData,
  }: {
    duration?: number;
    externalAuthData: TimeslotsStore.ExternalAuthData;
  }) {
    try {
      const {_items: timeSlots}: ApiListResponse<TimeslotData> =
        await apiRequest<CorePublicConferencesAvailableSlotsParams>({
          method: 'public.conferences.available_slots',
          options: {
            withAuth: false,
          },
          params: {
            client_id: externalAuthData.clientId,
            client_secret: externalAuthData.clientSecret,
            end_date: ISOStringToDatetimeObj(
              dayjs(this.calendarStore.range.end).toISOString(),
            ),
            partner_id: externalAuthData.partnerId,
            start_date: ISOStringToDatetimeObj(
              dayjs(this.calendarStore.range.start).toISOString(),
            ),
            token: externalAuthData.token,
          },
        });

      runInAction(() => {
        this.allSlots = timeSlots;
      });
    } finally {
      this.initialLoad.setDone();
    }
  }

  private async fetchNewCoachSlots(coachHistory: CoachHistoryExpandedCommon) {
    try {
      const startDate = dayjs.max(
        dayjs(datetimeObjToISOString(coachHistory.start_date)),
        dayjs(),
      );
      const endDate = dayjs(datetimeObjToISOString(coachHistory.end_date));

      // The case from bnrks code: it could happen when coach coverage just ended, but background task
      // haven't removed it yet, so end date could be earlier than now
      if (startDate.isAfter(endDate)) {
        return;
      }

      const {_items}: ApiListResponse<TimeslotData> = await apiRequest({
        method: 'client.conferences.available_slots',
        params: {
          coach_ids: [coachHistory.new_coach_id],
          end_date: ISOStringToDatetimeObj(endDate.toISOString()),
          start_date: ISOStringToDatetimeObj(startDate.toISOString()),
        },
      });

      runInAction(() => {
        this.newCoachSlots = _items;
      });
    } catch (e) {
      logger.error(e);
    }
  }

  private async fetchSlots({
    coaches,
    coachHistory,
    duration,
  }: {
    coaches: CoachModel[];
    coachHistory?: CoachHistoryExpandedCommon;
    duration?: number;
  }) {
    try {
      const startDate = this.getStartDate();
      const endDate = this.getEndDate(
        coachHistory
          ? [new CoachModel(coachHistory.old_coach as Coach)]
          : coaches,
      );

      // If the coach's availability ends before the start_date, we don't need to fetch
      if (startDate.isAfter(endDate)) {
        return;
      }

      const result: {_items: TimeslotData[]} =
        await apiRequest<CoreClientConferencesAvailableSlotsParams>({
          method: 'client.conferences.available_slots',
          params: {
            coach_ids: coachHistory
              ? [coachHistory.old_coach_id]
              : coaches.map(c => c.id),
            duration,
            end_date: ISOStringToDatetimeObj(
              this.getEndDate(
                coachHistory
                  ? [new CoachModel(coachHistory.old_coach as Coach)]
                  : coaches,
              ).toISOString(),
            ),
            start_date: ISOStringToDatetimeObj(
              this.getStartDate().toISOString(),
            ),
          },
        });

      runInAction(() => {
        this.allSlots = result._items;
      });
    } finally {
      this.initialLoad.setDone();
    }
  }

  // function that generate array in format 0_0, 0_15, 0_45 ... 23_45
  private generate24hTimesArray() {
    const result: string[] = [];

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += SCHEDULE_SLOT_DURATION) {
        result.push(`${hour}_${minute}`);
      }
    }

    return result;
  }

  private generateDaySlots(): DaySlots {
    const slotsMap = new Map<string, TimeSlot>();

    this.generate24hTimesArray().forEach(slot => {
      const [hour, minute] = slot.split('_');

      slotsMap.set(slot, {
        hour: Number(hour),
        isActive: false,
        minute: Number(minute),
      });
    });

    return slotsMap;
  }

  private getEndDate(coaches: CoachModel[]) {
    const coachScheduleUntilValues = coaches.map(c => c.bookingRange.end);

    const minCoachScheduleUntil = Math.min(...coachScheduleUntilValues);

    const endDateTimestamp = Math.min(
      minCoachScheduleUntil,
      this.calendarStore.range.end,
    );

    return dayjs(endDateTimestamp);
  }

  private getStartDate() {
    if (this.isTodayAvailableForBooking) {
      return dayjs(this.calendarStore.range.start);
    } else {
      return dayjs(this.calendarStore.range.start).add(1, 'day');
    }
  }

  private mapTimeslotDataToRenderSlotsMap(
    timeSlotData: TimeslotData[],
    withCoachId: boolean = false,
  ) {
    const localMap = new Map<string, DaySlots>();
    const nextRenderSlots: TimeslotsStore.RenderTimeslotsByDate = new Map();
    const startOfNextDayDate = dayjs().add(1, 'day').startOf('day');
    const minBookingDate = dayjs()
      .add(this.minBookingLeadMinutes, 'minutes')
      .toDate();

    timeSlotData
      .filter(timeslot => {
        const timeslotDjsDate = dayjs(
          datetimeObjToISOString(timeslot.start_date),
        );

        if (!this.isTodayAvailableForBooking) {
          return timeslotDjsDate.isAfter(startOfNextDayDate);
        }

        return true;
      })
      .forEach(ts => {
        const tsDjsDate = dayjs(datetimeObjToISOString(ts.start_date));
        const date = tsDjsDate.format(DATE_KEY_FORMAT);
        const hour = tsDjsDate.hour();
        const minute = tsDjsDate.minute();

        if (not(localMap.has(date))) {
          localMap.set(date, this.generateDaySlots());
        }

        const daySlots = localMap.get(date)!;

        const slotKey = `${hour}_${minute}`;

        if (daySlots.has(slotKey)) {
          daySlots.set(slotKey, {
            ...daySlots.get(slotKey)!,
            coachId: withCoachId ? ts.coach_ids[0] : undefined,
            isActive: tsDjsDate.toDate() >= minBookingDate,
          });
        }
      });

    localMap.forEach((value, key) => {
      const chunks = chunkArray(Array.from(value.values()), 4).filter(a =>
        a.some(item => item.isActive),
      );

      if (chunks.length === 0) {
        return;
      }

      nextRenderSlots.set(
        key,
        chunks.map(chunk =>
          chunk.map(
            item =>
              new TimeslotModel(
                ISOStringToDatetimeObj(
                  dayjs(key)
                    .add(item.hour, 'hour')
                    .add(item.minute, 'minute')
                    .toISOString(),
                ),
                item.isActive,
                item.coachId,
              ),
          ),
        ),
      );
    });

    return nextRenderSlots;
  }

  private mergeSlotsWithNewCoach(
    allSlots: TimeslotsStore.RenderTimeslotsByDate,
    newCoachSlots: TimeslotsStore.RenderTimeslotsByDate,
  ) {
    const result = new Map(allSlots);
    const newCoachDates = Array.from(newCoachSlots.keys());

    const newCoachStartDate = dayjs(newCoachDates[0]);
    const newCoachEndDate = dayjs(newCoachDates[newCoachDates.length - 1]);

    for (
      let date = newCoachStartDate;
      date.isBefore(newCoachEndDate);
      date = date.add(1, 'days')
    ) {
      result.delete(date.format(DATE_KEY_FORMAT));
    }

    for (const [date, slots] of newCoachSlots.entries()) {
      result.set(date, slots);
    }

    return result;
  }

  private updateSelectedSlotIfNeeded() {
    if (this.selected) {
      const selectedSlot = this.renderSlots
        .get(this.selected.startDateDjs.format(DATE_KEY_FORMAT))
        ?.flat()
        .find(slot => slot.id === this.selected?.id);

      if (selectedSlot) {
        this.selected = selectedSlot;
        this.selected.isSelected.setTrue();
      }
    }
  }
}

export namespace TimeslotsStore {
  export type BusyKey = 'makeSlots';

  export type ExternalAuthData = {
    clientId: string;
    clientSecret: string;
    partnerId: string;
    token: string;
  };

  export type RenderTimeslotsByDate = Map<string, TimeslotModel[][]>;
}
